import { useEffect, useMemo, useState } from "react";
import axios from "axios";
import "./contract-verify.css";

const fmt = (s) => (s ? new Date(s).toLocaleString("kk-KZ") : "—");

const badgeClass = (status) => {
  if (!status) return "t-badge t-badge--gray";
  if (status === "signed") return "t-badge t-badge--green";
  if (status === "signing") return "t-badge t-badge--blue";
  if (status === "rejected") return "t-badge t-badge--red";
  return "t-badge t-badge--yellow";
};

export default function ContractVerifyPage() {
  const [data, setData] = useState(null);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const token = params.get("token");

    if (!token) {
      setError("QR-токен табылмады");
      setLoading(false);
      return;
    }

    axios
      .get(`https://expertise.gov.kz/api/contracts/verify?token=${token}`)
      .then((res) => setData(res.data))
      .catch(() => setError("Деректерді алу кезінде қате орын алды"))
      .finally(() => setLoading(false));
  }, []);

  const signers = data?.signers ?? [];
  const signingStats = useMemo(() => {
    const total = signers.length;
    const done = signers.filter((s) => s.signed).length;
    return { total, done };
  }, [signers]);

  if (loading) return <div className="t-center">Жүктелуде...</div>;
  if (error)
    return (
      <div className="t-center text-red-600 font-semibold">{error}</div>
    );

  const contract = data?.contract;
  const order = data?.order;

  return (
    <div className="verify t-bg">
      <header className="t-header">
        <div className="t-header__brand">
          <div className="t-header__brand-title">
            expertise.gov.kz — Келісімшарт
          </div>
          <div className="t-header__brand-sub">
            Республикалық ғылыми-практикалық орталық
          </div>
        </div>

        <div className="t-header__status">
          <span className={badgeClass(contract?.status)}>
            {contract?.status === "signed"
              ? "Қол қойылған"
              : contract?.status === "signing"
              ? "Қол қоюда"
              : contract?.status || "—"}
          </span>
          <div className="t-progress">
            <div
              className="t-progress__bar"
              style={{
                width: `${(signingStats.done / Math.max(1, signingStats.total)) * 100}%`,
              }}
            />
          </div>
          <span className="t-progress__text">
            {signingStats.done}/{signingStats.total}
          </span>
        </div>
      </header>

      <main className="t-container">
        <div className="t-card">
          {/* Келісімшарт */}
          <section className="t-section">
            <h2 className="t-title">Келісімшарт мәліметтері</h2>
            <div className="t-grid t-grid--order">
              <div className="t-field">
                <div className="t-field__label">Нөмірі</div>
                <div className="t-field__value">
                  {contract?.contract_number || "—"}
                </div>
              </div>
              <div className="t-field">
                <div className="t-field__label">Күні</div>
                <div className="t-field__value">
                  {fmt(contract?.contract_date)}
                </div>
              </div>
              <div className="t-field">
                <div className="t-field__label">Сомасы (санмен)</div>
                <div className="t-field__value">
                  {contract?.amount_numeric || "—"} ₸
                </div>
              </div>
              <div className="t-field t-field--wide">
                <div className="t-field__label">Сомасы (қазақша)</div>
                <div className="t-field__muted">
                  {contract?.amount_text_kz || "—"}
                </div>
              </div>
              <div className="t-field t-field--wide">
                <div className="t-field__label">Сомасы (орысша)</div>
                <div className="t-field__muted">
                  {contract?.amount_text_ru || "—"}
                </div>
              </div>
            </div>
          </section>

          {/* Бұйрық */}
          <section className="t-section">
            <h2 className="t-title">Байланысты бұйрық</h2>
            <div className="t-grid t-grid--order">
              <div className="t-field">
                <div className="t-field__label">Нөмірі</div>
                <div className="t-field__value">
                  {order?.order_number || "—"}
                </div>
              </div>
              <div className="t-field t-field--wide">
                <div className="t-field__label">Тақырыбы</div>
                <div className="t-field__value">{order?.title || "—"}</div>
              </div>
              <div className="t-field">
                <div className="t-field__label">Күйі</div>
                <span className={badgeClass(order?.status)}>
                  {order?.status === "signed"
                    ? "Қол қойылған"
                    : order?.status || "—"}
                </span>
              </div>
            </div>
          </section>

          {/* Қол қоюшылар */}
          <section className="t-section">
            <h2 className="t-title">Қол қоюшылар</h2>
            <ol className="t-timeline">
              {signers.map((s) => {
                const state = s.signed ? "green" : "gray";
                return (
                  <li
                    key={s.user_id}
                    className={`t-timeline__item t-timeline__item--${state}`}
                  >
                    <div className="t-timeline__badge" />
                    <div className="t-signer">
                      <div className="t-signer__name">{s.full_name}</div>
                      <div className="t-signer__role">{s.role}</div>
                      <div className="t-signer__status">
                        {s.signed ? (
                          <span className="t-text--green">
                            ✅ Қол қойылды: {fmt(s.signed_at)}
                          </span>
                        ) : (
                          <span className="t-text--muted">⏳ Күтуде</span>
                        )}
                      </div>
                    </div>
                  </li>
                );
              })}
            </ol>
          </section>
        </div>

        <footer className="t-footer">
          © {new Date().getFullYear()} БМСО · expertise.gov.kz
        </footer>
      </main>
    </div>
  );
}
